import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TestEmailAddress } from '@lsq-frontend/angular-shared/app-constants';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import {
    FastTrackRouteQueryParams,
    UserStates
} from '@lsq-frontend/fasttrack-shared/app-constants';
import { MfaBackupCodesService } from '@lsq-frontend/fasttrack-shared/features/mfa-backup-codes';
import { UserStateService } from '@lsq-frontend/fasttrack-shared/features/user-state-service';
import { atlas, LOGIN, LOGOUT } from '@lsq-frontend/fasttrack-shared/routing-atlas';
import {
    Environment,
    User,
    UserStateStatus,
    UserStatus
} from '@lsq-frontend/remote-services/enterprise/common';
import {
    CreateUserForgotPasswordCodeRequest,
    CreateUserForgotPasswordCodeService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import {
    AuthenticationService,
    IAuthenticationResult,
    MFAType,
    Modal,
    ModalService,
    getRouterLinkById,
    getRouterLinkUrlById
} from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { BehaviorSubject } from 'rxjs';
import { TroubleSigningInModalComponent } from './login-steps/trouble-signing-in-modal.component';

export enum LoginPageStep {
    SignIn,
    Mfa,
    MfaRegistration,
    SmsMfaRegistration,
    TotpMfaRegistration,
    BackupCode,
    ForgotPassword,
    ForgotPasswordVerification,
    ForcedResetPassword
}

@Injectable()
export class LoginService {
    private _loginPageStepSubject = new BehaviorSubject<LoginPageStep>(LoginPageStep.SignIn);
    public loginPageStep$ = this._loginPageStepSubject.asObservable();

    public routeParams$ = this._activatedRoute.queryParamMap;

    private _phoneNumberLastFourSubject = new BehaviorSubject<string>(null);
    public phoneNumberLastFour$ = this._phoneNumberLastFourSubject.asObservable();
    private _mfaTypeSubject = new BehaviorSubject<MFAType>(null);
    public mfaType$ = this._mfaTypeSubject.asObservable();
    public mfaType: MFAType;

    public cachedEmailAddress: string;
    private _cachedPassword: string;
    private _userIsRegisteredForMfa = false;
    public displayPasswordReset = false;
    public currentUser: User;
    public qrCode: string;

    constructor(
        @Inject(SETTINGS) private _settings: ISettings,
        private _i18n: I18n,
        private _router: Router,
        private _modalService: ModalService,
        private _activatedRoute: ActivatedRoute,
        private _authProviderService: AuthenticationService,
        private _userService: UserService,
        private _mfaBackupCodesService: MfaBackupCodesService,
        private _userStateService: UserStateService,
        private _createUserForgotPasswordCodeService: CreateUserForgotPasswordCodeService
    ) {
        this._userService.logout$.subscribe(() => {
            this.setLoginPageStep(LoginPageStep.SignIn);
        });
    }

    public set userIsRegisteredForMfa(isRegistered: boolean) {
        this._userIsRegisteredForMfa = isRegistered;
    }

    public setLoginPageStep(step: LoginPageStep): void {
        this._loginPageStepSubject.next(step);
    }

    public async finishLogin(showUpdateMfaModal = false): Promise<void> {
        const showUpdateMfaModalParam: boolean = showUpdateMfaModal;
        await this._userService.getAndEmitCurrentUser();
        if (this._userIsRegisteredForMfa) {
            const userHasValidBackupCodes = await this._mfaBackupCodesService.checkUserHasBackupCodes();
            if (userHasValidBackupCodes) {
                if (!showUpdateMfaModal) {
                    void this._userService.navigateToHome();
                } else {
                    void this._userService.navigateToHome({
                        showUpdateMfaModal: showUpdateMfaModalParam.toString()
                    });
                }
            }
        } else {
            void this._userService.navigateToHome({
                showUpdateMfaModal: showUpdateMfaModalParam.toString()
            });
        }
        this._userIsRegisteredForMfa = false;
    }

    public async finishLoginForTOTP(): Promise<void> {
        await this._userService.getAndEmitCurrentUser();
        if (this._userIsRegisteredForMfa) {
            const userHasValidBackupCodes = await this._mfaBackupCodesService.checkUserHasBackupCodes();
            if (userHasValidBackupCodes) {
                void this._userService.navigateToHome();
            }
        } else {
            void this._userService.navigateToHome();
        }
        this._userIsRegisteredForMfa = false;
    }

    public async refreshUser(): Promise<void> {
        this.setLoginPageStep(LoginPageStep.MfaRegistration);
    }

    public async loginWithEmailAndPassword(emailAddress: string, password: string): Promise<void> {
        // Login for test@lsq.com or Demo env.
        if (
            (this._settings.environment !== Environment.Production &&
                emailAddress === TestEmailAddress) ||
            !this._authProviderService.isOn()
        ) {
            await this._userService.authenticateUserForTestAndDemo(emailAddress, password);
            await this.finishLogin();
            return;
        }

        const authProviderResult = await this._authProviderService.authenticateUserWithEmailAndPassword(
            emailAddress,
            password
        );

        this.cachedEmailAddress = emailAddress;
        this._cachedPassword = password;

        if (authProviderResult.requiresPasswordReset) {
            this.setLoginPageStep(LoginPageStep.ForcedResetPassword);
        } else if (authProviderResult.authorized) {
            // Call LSQ auth service.
            const user = await this._userService.authenticateUser(
                emailAddress,
                authProviderResult.accessToken
            );

            if (user?.resetPassword) {
                throw new ErrorEvent('Password reset required, please check your email.');
            } else if (
                user?.userStatus === UserStatus.MultiFactorAuthenticationSetupRequired &&
                !(await this._isMidEnrollment())
            ) {
                if (this.cachedEmailAddress.endsWith('@lsq.com')) {
                    await this.finishLogin();
                } else {
                    this.setLoginPageStep(LoginPageStep.MfaRegistration);
                }
            } else {
                await this.finishLogin();
            }
        } else if (authProviderResult.requiresAuthenticationCode) {
            this._userIsRegisteredForMfa = true;
            this._mfaTypeSubject.next(authProviderResult.mfaType);
            this._phoneNumberLastFourSubject.next(authProviderResult.phoneNumberLast4);
            this.setLoginPageStep(LoginPageStep.Mfa);
        } else {
            throw new ErrorEvent('Invalid email address/password. Please try again.');
        }
    }

    public async resendMfaCode(): Promise<void> {
        await this._authProviderService.authenticateUserWithEmailAndPassword(
            this.cachedEmailAddress,
            this._cachedPassword
        );
    }

    public async handleUseTOTP(): Promise<void> {
        this._mfaTypeSubject.next(MFAType.TOTP);
        this.setLoginPageStep(LoginPageStep.Mfa);
    }

    public async handleSuccessfulMfa(password: string): Promise<void> {
        const userResponse = await this._userService.authenticateUser(
            this.cachedEmailAddress,
            password
        );

        if (userResponse?.resetPassword) {
            this.setLoginPageStep(LoginPageStep.SignIn);
            void this._router.navigate(getRouterLinkById(atlas, LOGIN), {
                queryParams: { [FastTrackRouteQueryParams.ShowPasswordReset]: true }
            });
        }

        await this.finishLogin();
    }

    public async submitBackupCode(backupCode: string): Promise<void> {
        try {
            const authProviderResult = await this._authProviderService.authenticateUserWithBackupCode(
                this.cachedEmailAddress,
                backupCode
            );

            if (authProviderResult.authorized) {
                // Call LSQ auth service.
                await this._userService.authenticateUser(
                    this.cachedEmailAddress,
                    authProviderResult.accessToken
                );

                await this.finishLogin(true);
            } else {
                throw new ErrorEvent('Invalid backup code.');
            }
        } catch {
            throw new ErrorEvent('Invalid backup code.');
        }
    }

    public async sendForgotPasswordEmail(emailAddress: string): Promise<void> {
        await this._authProviderService.sendForgotPasswordEmail(emailAddress);
    }

    public async sendEnterpriseForgotPasswordEmail(emailAddress: string): Promise<void> {
        this._createUserForgotPasswordCodeService.execute(
            new CreateUserForgotPasswordCodeRequest({ emailAddress })
        );
    }

    public async completeForgotPasswordReset(
        emailAddress: string,
        verificationCode: string,
        newPassword: string
    ): Promise<IAuthenticationResult> {
        return await this._authProviderService.resetForgottenPassword(
            emailAddress,
            verificationCode,
            newPassword
        );
    }

    public async completeForcedPasswordReset(
        emailAddress: string,
        verificationCode: string,
        newPassword: string
    ): Promise<void> {
        const result = await this._authProviderService.resetPasswordForForcedReset(
            emailAddress,
            verificationCode,
            newPassword
        );
        if (result.authorized) {
            const user = await this._userService.authenticateUser(emailAddress, result.accessToken);

            if (user.userStatus === UserStatus.MultiFactorAuthenticationSetupRequired) {
                this.setLoginPageStep(LoginPageStep.MfaRegistration);
            } else {
                await this.finishLogin();
            }
        } else if (result.requiresAuthenticationCode) {
            this._userIsRegisteredForMfa = true;
            this.setLoginPageStep(LoginPageStep.Mfa);
            this._phoneNumberLastFourSubject.next(result.phoneNumberLast4);
        } else {
            throw new ErrorEvent('Invalid login attempt.');
        }
    }

    public async showTroubleSigningInModal(): Promise<void> {
        this._modalService.showModal(
            new Modal({
                title: this._i18n('Having trouble signing in?'),
                bodyComponent: TroubleSigningInModalComponent,
                showCancelButton: true,
                showCloseButton: true,
                showFooter: true,
                showOkButton: true,
                okButtonText: this._i18n('Back to Login'),
                okButtonSize: 'block',
                width: '62rem',
                onOkClick: async () => {
                    void this._router.navigateByUrl(getRouterLinkUrlById(atlas, LOGOUT));
                    this._modalService.closeModal();
                }
            })
        );
    }

    private async _isMidEnrollment(): Promise<boolean> {
        const enrollmentVerifyEmailState = await this._userStateService.getUserState(
            UserStates.EnrollmentVerifyEmail
        );
        const enrollmentMfaState = await this._userStateService.getUserState(
            UserStates.EnrollmentMfa
        );
        const enrollmentSupplierAgreementState = await this._userStateService.getUserState(
            UserStates.EnrollmentSupplierAgreement
        );
        const enrollmentBankAccountState = await this._userStateService.getUserState(
            UserStates.EnrollmentBankAccount
        );

        return (
            (!!enrollmentVerifyEmailState &&
                enrollmentVerifyEmailState.userStateStatus !== UserStateStatus.Done) ||
            (!!enrollmentMfaState && enrollmentMfaState.userStateStatus !== UserStateStatus.Done) ||
            (!!enrollmentSupplierAgreementState &&
                enrollmentSupplierAgreementState.userStateStatus !== UserStateStatus.Done) ||
            (!!enrollmentBankAccountState &&
                enrollmentBankAccountState.userStateStatus !== UserStateStatus.Done)
        );
    }
}
